import 'react-loading-skeleton/dist/skeleton.css';

import {
  AgendaItemsRoot,
  EventName,
  RightSideBox,
  SessionDescription,
  SessionDescriptionText,
  SessionDetailsItem,
  SessionDetailsSection,
  SessionDetailsText,
  SessionTitle,
  SessionTitleWrapper,
  SpeakersWrapper,
  Tag,
  Tags,
} from './styles';
import {
  PublicEventDetailsResponse,
  PublicSessionItem,
  PublicTrackItem,
} from '@hubs101/booking-api-skd-client/lib/types';

import { Column } from '@components/LinearLayout';
import Icon from '@components/Icon';
import Skeleton from 'react-loading-skeleton';
import SpeakerItem from '@composites/SpeakerItem';
import { capitalizeFirstLetter } from '@utils/fn';
import dayjs from 'dayjs';
import { getEventDetails } from '@store/slices/eventSlice';
import { sanitizeHtmlWithoutTargetAttrInAnchor } from '@utils/sanitize';
import { useMemo } from 'react';
import { usePublicCoLocatedEventsByEventId } from '@utils/apiHooks';
import { useSelector } from 'react-redux';
import { useTabletWidth } from '@utils/hooks';
import { useTheme } from 'styled-components';

interface AgendaItemProps {
  agendasTracks?: PublicTrackItem[];
  isLoading?: boolean;
  session?: PublicSessionItem;
  minimalDetails?: boolean;
  showDescriptionOnMobile?: boolean;
}

const AgendaItem = ({
  session,
  agendasTracks,
  isLoading,
  minimalDetails,
  showDescriptionOnMobile,
}: AgendaItemProps) => {
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const isTablet = useTabletWidth();

  const { data: coLocatedEvents, isLoading: isCoLocatedEventsLoading } =
    usePublicCoLocatedEventsByEventId({
      eventId: sluggedEvent?.id,
      enabled: sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated,
    });

  const getSessionEventId = (s: any): string | undefined => {
    const e = s?.event;
    if (!e) return undefined;
    if (typeof e === 'string') return e;
    if (typeof e === 'object') return e.id || e.event_id || e.eventId; // { id: "uuid" }
    return undefined;
  };

  const isAgendaLoading = isLoading ?? isCoLocatedEventsLoading;
  const eventFromSession = useMemo(() => {
    if (sluggedEvent?.colocated) {
      const sid = getSessionEventId(session);
      const found = coLocatedEvents?.find(e => e.id === sid);
      return found ?? sluggedEvent;
    }
    return sluggedEvent;
  }, [sluggedEvent?.colocated, session, coLocatedEvents, sluggedEvent]);

  const targetEventSlug =
    sluggedEvent?.slug ||
    (typeof session?.event === 'object' && (session?.event as any)?.slug) ||
    eventFromSession?.slug;

  const sessionDetailsItems = useMemo(() => {
    if (minimalDetails) {
      return [
        {
          icon: 'clock',
          value: `${dayjs(session?.start_date).format('HH:mm')} - ${dayjs(session?.end_date).format('HH:mm')}`,
        },
      ];
    }

    return [
      { icon: 'agenda', value: capitalizeFirstLetter(String(session?.type)) },
      {
        icon: 'clock',
        value: `${dayjs(session?.start_date).format('HH:mm')} - ${dayjs(session?.end_date).format('HH:mm')}`,
      },
      { icon: 'location', value: session?.location?.name },
      { icon: 'expectations', value: 'Solution Study' },
    ];
  }, [
    minimalDetails,
    session?.type,
    session?.start_date,
    session?.end_date,
    session?.location?.name,
  ]);

  const allowedEventIds = useMemo(
    () =>
      [sluggedEvent?.id, ...(coLocatedEvents?.map(e => e.id) ?? [])].filter(
        Boolean
      ),
    [sluggedEvent?.id, coLocatedEvents]
  );

  const sessionEventId = getSessionEventId(session);
  const sourceEventIdForPrevious =
    sessionEventId && sessionEventId !== sluggedEvent?.id
      ? sessionEventId
      : undefined;

  return (
    <article>
      <AgendaItemsRoot>
        {isTablet && (
          <>
            {isAgendaLoading ? (
              <Skeleton
                count={1}
                enableAnimation
                width="100%"
                height="9rem"
                borderRadius={theme.borderRadius.radius10}
              />
            ) : (
              <SessionTitleWrapper
                $color={eventFromSession?.design?.themeColor}
              >
                <SessionTitle>{session?.title}</SessionTitle>
              </SessionTitleWrapper>
            )}
          </>
        )}

        {isAgendaLoading ? (
          <Skeleton
            count={1}
            enableAnimation
            width="16rem"
            height="33rem"
            borderRadius={theme.borderRadius.radius10}
          />
        ) : (
          <SessionDetailsSection
            $color={eventFromSession?.design?.themeColor}
            $minimal={minimalDetails}
          >
            {sessionDetailsItems?.map(({ icon, value }) => (
              <SessionDetailsItem key={icon} $minimal={minimalDetails}>
                <Icon
                  size={22}
                  name={icon}
                  color={theme.color.white}
                  indentLess
                />
                <SessionDetailsText>{value}</SessionDetailsText>
              </SessionDetailsItem>
            ))}

            {!minimalDetails && (
              <EventName
                $backgroundColor={eventFromSession?.design?.secondaryThemeColor}
              >
                {eventFromSession?.name}
              </EventName>
            )}
          </SessionDetailsSection>
        )}

        <RightSideBox $minimal={minimalDetails}>
          <SessionDescription>
            {!isTablet && (
              <>
                {isAgendaLoading ? (
                  <Skeleton
                    count={1}
                    enableAnimation
                    width="100%"
                    height="9rem"
                    borderRadius={theme.borderRadius.radius10}
                  />
                ) : (
                  <SessionTitleWrapper
                    $color={eventFromSession?.design?.themeColor}
                  >
                    <SessionTitle>{session?.title}</SessionTitle>
                  </SessionTitleWrapper>
                )}
              </>
            )}

            <SpeakersWrapper>
              {session?.speakers?.map(speaker => (
                <SpeakerItem
                  key={speaker.profile}
                  speaker={speaker}
                  color={eventFromSession?.design?.themeColor}
                  isAgendaLoading={isAgendaLoading}
                  eventSlug={targetEventSlug}
                  sourceEventIdForPrevious={sourceEventIdForPrevious}
                />
              ))}
            </SpeakersWrapper>
            {(!isTablet || showDescriptionOnMobile) && (
              <>
                {isAgendaLoading ? (
                  <Column $gap="1rem">
                    <Skeleton enableAnimation width="100%" />
                    <Skeleton enableAnimation width="50%" />
                    <Skeleton enableAnimation width="80%" />
                  </Column>
                ) : (
                  <SessionDescriptionText
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtmlWithoutTargetAttrInAnchor(
                        String(session?.description)
                      ),
                    }}
                  />
                )}
              </>
            )}
          </SessionDescription>
          {isAgendaLoading ? (
            <Tags>
              <Skeleton
                enableAnimation
                width="5rem"
                borderRadius={theme.borderRadius.radius20}
              />
              <Skeleton
                enableAnimation
                width="7rem"
                borderRadius={theme.borderRadius.radius20}
              />
              <Skeleton
                enableAnimation
                width="6rem"
                borderRadius={theme.borderRadius.radius20}
              />
            </Tags>
          ) : (
            <Tags>
              {session?.tracks?.map((track: string) => (
                <Tag
                  $color={eventFromSession?.design?.secondaryThemeColor}
                  key={track}
                >
                  {agendasTracks?.find(e => e.id === track)?.name}
                </Tag>
              ))}
            </Tags>
          )}
        </RightSideBox>
      </AgendaItemsRoot>
    </article>
  );
};

export default AgendaItem;
